import React from 'react';
import {
  Box,
  Switch,
  TextField,
  ToggleButton,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import type {
  IAssistantScript,
  TriggerInterruption,
  TriggerType,
} from '@shared/interfaces/assistantScript';
import { PlaybackPolicy } from '@shared/interfaces/assistantScript';
import type { PollyLanguage } from '@shared/interfaces/polly';
import { languageFlags, languageLabels } from '../constants';
import {
  ScriptToggleButtonGroup,
  TriggerSectionSubtitle,
  TriggerSectionTitle,
} from '../AssistantScriptDialog.styled';
import {
  AllowSkipBox,
  AllowSkipDescription,
  AllowSkipTitle,
  NoaPanelPreview,
  NoaPanelPreviewButton,
  NoaPanelPreviewLabel,
  TriggerAreaCard,
  TriggerAreaCardLabel,
  TriggerAreaCardObjectIcon,
  TriggerAreaCardPinIcon,
  TriggerAreaCardTitle,
  TriggerInfoBox,
  TriggerTogglePinIcon,
  TriggerToggleTouchIcon,
  UiClickSectionLabel,
} from './styled';
import InterruptSection from '../InterruptSection';
import PlaybackPolicySection from '../PlaybackPolicySection';

interface TriggerSectionProps {
  triggerType: TriggerType;
  title?: string;
  assistantScript?: IAssistantScript | null;
  mapName?: string;
  activeLanguage: PollyLanguage;
  effectiveBaseLanguage: PollyLanguage;
  uiClickButtonLabel: string;
  uiClickButtonLabelTranslations: Partial<Record<PollyLanguage, string>>;
  languageTabs?: React.ReactNode;
  interruptMode: TriggerInterruption;
  bridgeRegionGaps: boolean;
  allowSkipDialog: boolean;
  playbackPolicy: PlaybackPolicy;
  skipFirstSession: boolean;
  onTriggerTypeChange: (type: TriggerType) => void;
  onUiClickButtonLabelChange: (label: string) => void;
  onUiClickButtonLabelTranslationChange: (
    lang: PollyLanguage,
    label: string,
  ) => void;
  onInterruptModeChange: (mode: TriggerInterruption) => void;
  onBridgeRegionGapsChange: (bridge: boolean) => void;
  onAllowSkipDialogChange: (allow: boolean) => void;
  onPlaybackPolicyChange: (policy: PlaybackPolicy) => void;
  onSkipFirstSessionChange: (skip: boolean) => void;
}

const TriggerSection = ({
  triggerType,
  title,
  assistantScript,
  mapName,
  activeLanguage,
  effectiveBaseLanguage,
  uiClickButtonLabel,
  uiClickButtonLabelTranslations,
  languageTabs,
  interruptMode,
  bridgeRegionGaps,
  allowSkipDialog,
  playbackPolicy,
  skipFirstSession,
  onTriggerTypeChange,
  onUiClickButtonLabelChange,
  onUiClickButtonLabelTranslationChange,
  onInterruptModeChange,
  onBridgeRegionGapsChange,
  onAllowSkipDialogChange,
  onPlaybackPolicyChange,
  onSkipFirstSessionChange,
}: TriggerSectionProps) => {
  const { t } = useTranslation('common');
  const displayTitle = title ?? assistantScript?.title;
  const isBaseLang = activeLanguage === effectiveBaseLanguage;
  const currentButtonLabel = isBaseLang
    ? uiClickButtonLabel
    : uiClickButtonLabelTranslations[activeLanguage] ?? '';

  return (
    <Box>
      <TriggerSectionTitle>
        {t('assistant.scriptDialog.trigger')}
      </TriggerSectionTitle>
      <TriggerSectionSubtitle>
        {t('assistant.scriptDialog.triggerSubtitle')}
      </TriggerSectionSubtitle>

      <TriggerAreaCard>
        {triggerType === 'object' ? (
          <TriggerAreaCardObjectIcon />
        ) : (
          <TriggerAreaCardPinIcon />
        )}
        <Box>
          <TriggerAreaCardLabel>
            {triggerType === 'object'
              ? t('assistant.scriptDialog.triggerObject')
              : triggerType === 'custom_spot'
              ? t('assistant.scriptDialog.triggerCustomSpot')
              : t('assistant.scriptDialog.triggerArea')}
          </TriggerAreaCardLabel>
          <TriggerAreaCardTitle>{displayTitle}</TriggerAreaCardTitle>
          {mapName && (
            <TriggerAreaCardLabel sx={{ display: 'none' }}>
              {mapName}
            </TriggerAreaCardLabel>
          )}
        </Box>
      </TriggerAreaCard>

      {triggerType !== 'object' && (
        <>
          {triggerType !== 'custom_spot' && (
            <ScriptToggleButtonGroup
              value={triggerType}
              exclusive
              onChange={(_, value) => {
                if (value !== null) {
                  onTriggerTypeChange(value as TriggerType);
                }
              }}
              size="small"
              sx={{ mb: 2 }}
            >
              <ToggleButton value="area_entry">
                <TriggerTogglePinIcon />{' '}
                {t('assistant.scriptDialog.triggerAreaEntry')}
              </ToggleButton>
              <ToggleButton value="ui_click">
                <TriggerToggleTouchIcon />{' '}
                {t('assistant.scriptDialog.triggerUiClick')}
              </ToggleButton>
            </ScriptToggleButtonGroup>
          )}

          {triggerType === 'area_entry' ? (
            <TriggerInfoBox>
              <Typography variant="body2" color="text.secondary">
                {t('assistant.scriptDialog.triggerAreaEntryDescPre')}{' '}
                <strong>{displayTitle}</strong>.
              </Typography>
            </TriggerInfoBox>
          ) : (
            <TriggerInfoBox>
              <UiClickSectionLabel>
                {t('assistant.scriptDialog.buttonInNoaDialog')}
              </UiClickSectionLabel>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                {t('assistant.scriptDialog.buttonInNoaDialogDescPre')}{' '}
                <strong>{displayTitle}</strong>.{' '}
                {t('assistant.scriptDialog.buttonInNoaDialogDescPost')}
              </Typography>
              {languageTabs}
              {!isBaseLang && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1.5, mt: 1.5 }}
                >
                  {languageFlags[effectiveBaseLanguage]}{' '}
                  {t('assistant.scriptDialog.baseLang', {
                    language: languageLabels[effectiveBaseLanguage],
                  })}
                  :{' '}
                  {uiClickButtonLabel ||
                    t('assistant.scriptDialog.buttonLabel')}
                </Typography>
              )}
              <TextField
                fullWidth
                size="small"
                placeholder={
                  isBaseLang
                    ? t('assistant.scriptDialog.buttonLabel')
                    : t('assistant.scriptDialog.enterTranslation', {
                        language: languageLabels[activeLanguage],
                      })
                }
                value={currentButtonLabel}
                onChange={(e) =>
                  isBaseLang
                    ? onUiClickButtonLabelChange(e.target.value)
                    : onUiClickButtonLabelTranslationChange(
                        activeLanguage,
                        e.target.value,
                      )
                }
                sx={{ mb: 1.5, mt: !isBaseLang ? 0 : 2 }}
              />
              <NoaPanelPreview>
                <NoaPanelPreviewLabel>
                  {t('assistant.scriptDialog.previewInNoaPanel')}
                </NoaPanelPreviewLabel>
                <NoaPanelPreviewButton>
                  {currentButtonLabel ||
                    uiClickButtonLabel ||
                    t('assistant.scriptDialog.buttonLabel')}
                </NoaPanelPreviewButton>
              </NoaPanelPreview>
            </TriggerInfoBox>
          )}

          {triggerType === 'area_entry' && (
            <PlaybackPolicySection
              playbackPolicy={playbackPolicy}
              onPlaybackPolicyChange={onPlaybackPolicyChange}
              skipFirstSession={skipFirstSession}
              onSkipFirstSessionChange={onSkipFirstSessionChange}
            />
          )}

          <InterruptSection
            interruptMode={interruptMode}
            onInterruptModeChange={onInterruptModeChange}
            bridgeRegionGaps={bridgeRegionGaps}
            onBridgeRegionGapsChange={onBridgeRegionGapsChange}
          />

          <AllowSkipBox>
            <Switch
              checked={allowSkipDialog}
              onChange={(e) => onAllowSkipDialogChange(e.target.checked)}
            />
            <Box>
              <AllowSkipTitle>
                {t('assistant.scriptDialog.allowSkipDialog')}
              </AllowSkipTitle>
              <AllowSkipDescription>
                {t('assistant.scriptDialog.allowSkipDialogDesc')}
              </AllowSkipDescription>
            </Box>
          </AllowSkipBox>
        </>
      )}
    </Box>
  );
};

export default TriggerSection;
